import React, { useState, useEffect } from "react";
import './Login.scss';
import axios from 'axios';
import { resendOtp, resendOtpByEmail, resendOtpByPhone } from '../../api/messaging';
import { useNavigate, Link } from "react-router-dom";
import { useNoProfile } from "../../hooks/useProfileData";
import { useProfile } from "../../Contexts/ProfileContext";
import { GuestForm } from "../GuestForm";
import { clearGuestData } from "../../utils/storage";
import { useToast } from "../../Contexts/ToastContext";
import { GoogleSignInButton } from "../GoogleSignInButton";
import { noAutofillProps } from "../noAutofillProps";
import { requestLoginOtpAndNavigate } from "../otpCooldown";
import { isValidLoginEmail } from "../loginIdentifier";
import { usePasswordSetCheck } from "../usePasswordSetCheck";
import { ForgotPasswordModal } from "../ForgotPasswordModal";
import { fetchSiteMedia, mediaUrl } from "../../utils/siteMedia";


export function Login() {
  useNoProfile();
  const { fetchProfile, updateProfileData } = useProfile();
  const { showToast } = useToast();
  const [authBackground, setAuthBackground] = useState('');

  useEffect(() => {
    localStorage.removeItem('resendTime');
  }, []);

  useEffect(() => {
    let cancelled = false;
    fetchSiteMedia().then((assets) => {
      if (!cancelled) setAuthBackground(mediaUrl(assets, 'login.background') || '');
    });
    return () => { cancelled = true; };
  }, []);

  const [formData, setFormData] = useState({
    phone: '',
    email: '',
    password: ''
  });

  const [loading, setLoading] = useState(false);
  const [responseMessage, setResponseMessage] = useState('');
  const [showGuestForm, setShowGuestForm] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [responseIsSuccess, setResponseIsSuccess] = useState(false);
  const navigate = useNavigate();

  const hasValidPhone = formData.phone.length === 10;
  const hasValidEmail = isValidLoginEmail(formData.email);
  const hasValidIdentifier = hasValidPhone || hasValidEmail;
  const {
    hasPasswordSet,
    isPasswordCheckReady,
    isPasswordCheckPending,
    usesPhoneIdentifier,
  } = usePasswordSetCheck(formData.phone, formData.email);
  const showPasswordField = isPasswordCheckReady && hasPasswordSet === true;
  const showOtpAlternatives = isPasswordCheckReady && (hasPasswordSet !== true || !formData.password.trim());

  const getSubmitButtonLabel = () => {
    if (loading) {
      if (hasPasswordSet === false) {
        return usesPhoneIdentifier ? 'Sending OTP...' : 'Sending email code...';
      }
      return 'Logging in...';
    }
    if (isPasswordCheckPending) {
      return 'Checking...';
    }
    if (isPasswordCheckReady && hasPasswordSet === false) {
      return usesPhoneIdentifier ? 'Continue with OTP' : 'Continue with email code';
    }
    return 'LOGIN';
  };

  useEffect(() => {
    if (hasPasswordSet !== true) {
      setFormData((prev) => (prev.password ? { ...prev, password: '' } : prev));
    }
  }, [hasPasswordSet]);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    if (name === 'phone') {
      const phone = value.replace(/\D/g, '').slice(0, 10);
      setFormData((prev) => ({
        ...prev,
        phone,
        ...(phone ? { email: '' } : {}),
      }));
      return;
    }
    if (name === 'email') {
      setFormData((prev) => ({
        ...prev,
        email: value,
        ...(value.trim() ? { phone: '' } : {}),
      }));
      return;
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!hasValidIdentifier) {
      setResponseIsSuccess(false);
      setResponseMessage('Please enter a valid 10-digit mobile number or email address.');
      return;
    }

    if (hasPasswordSet === true && !formData.password.trim()) {
      setResponseIsSuccess(false);
      setResponseMessage('Password is required.');
      return;
    }

    setLoading(true);
    setResponseIsSuccess(false);
    setResponseMessage('');

    try {
      if (hasPasswordSet === false) {
        const type = hasValidPhone ? 'phone' : 'email';
        const identifier = hasValidPhone ? formData.phone : formData.email.trim();
        await requestLoginOtpAndNavigate({
          type,
          identifier,
          navigate,
          sendFn: () => resendOtp(
            hasValidPhone
              ? { phone: formData.phone, purpose: 'login' }
              : { email: formData.email.trim(), purpose: 'login' }
          ),
        });
      } else {
        const payload = {
          password: formData.password,
          ...(hasValidPhone ? { phone: formData.phone } : { email: formData.email.trim() })
        };
        const response = await axios.post('/users/login', payload);
        if (response.status === 200 && response.data?.data) {
          const token = response.data.data.token;
          if (token) {
            clearGuestData();
            localStorage.setItem('token', token);
            localStorage.setItem('passwordSet', 'true'); // User logged in with password
            await fetchProfile(true);
            updateProfileData({ data: { passwordSet: true } });
          }
          navigate('/');
        }
      }
    } catch (error) {
      console.error('Login error:', error);
      const msg = error.response?.data?.msg || error.response?.data?.message;
      setResponseIsSuccess(false);
      setResponseMessage(msg || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const openForgotPassword = () => {
    setShowForgotPassword(true);
  };

  const isBusy = loading || isPasswordCheckPending;

  return (
    <div className={`web-authPage${isBusy ? ' auth-busy' : ''}`} aria-busy={isBusy}>
      <div className="auth-container">
        <div className="LazyLoad is-visible auth-hero">
          {authBackground ? (
          <img
            src={authBackground}
            className="img-responsive preLoad loaded"
            alt=""
          />
          ) : null}
        </div>
        <div className="signInContainer">
          <div className="welcome-header">
            Login
            <span className="welcome-header-small">Welcome back!</span>
          </div>
          <div className="mobileInputContainer">
            <form onSubmit={handleSubmit} autoComplete="off">
              <div className="auth-identifier-group">
                <div className="form-group">
                  <input
                    type="tel"
                    name="phone"
                    className="form-control mobileNumberInput"
                    placeholder="Mobile Number"
                    maxLength="10"
                    value={formData.phone}
                    onChange={handleInputChange}
                    disabled={isBusy}
                    inputMode="numeric"
                    {...noAutofillProps()}
                  />
                <span className="placeholderAlternative mobileNumber">
                  +91
                  <span style={{ padding: '0px 10px', position: 'relative', bottom: '1px' }}>|</span>
                  Mobile Number <span className="field-optional-label">(or email below)</span>
                </span>
                <i className="bar"></i>
              </div>

              <div className="auth-field-or">OR</div>

              <div className="form-group">
                <input
                  type="email"
                  name="email"
                  className="form-control mobileNumberInput"
                  placeholder="Email Address"
                  value={formData.email}
                  onChange={handleInputChange}
                  disabled={isBusy}
                  {...noAutofillProps()}
                />
                <span className="placeholderAlternative mobileNumber">
                  Email Address <span className="field-optional-label">(or mobile above)</span>
                </span>
                <i className="bar"></i>
              </div>
              </div>

              {showPasswordField && (
              <div className="form-group">
                <input
                  type="password"
                  name="password"
                  className="form-control mobileNumberInput"
                  placeholder="Password"
                  value={formData.password}
                  onChange={handleInputChange}
                  disabled={isBusy}
                  {...noAutofillProps({ autoComplete: 'new-password' })}
                />
                <span className="placeholderAlternative mobileNumber">
                  Password <span style={{ color: '#ff5722' }}>*</span>
                </span>
                <i className="bar"></i>
              </div>
              )}
              {showPasswordField && (
              <div className="login-forgot-password-wrap">
                <button type="button" className="login-forgot-password-link" onClick={openForgotPassword}>
                  Forgot password?
                </button>
              </div>
              )}

              {showOtpAlternatives && (
              <div className="login-otp-options">
                <span className="login-otp-options__label">Or verify using</span>
                <div className="login-otp-options__links">
                  <button
                    type="button"
                    className={`login-otp-options__btn ${formData.phone.length === 10 ? '' : 'login-otp-options__btn--disabled'}`}
                    disabled={formData.phone.length !== 10 || loading}
                    onClick={async () => {
                      if (formData.phone.length !== 10 || loading) return;
                      setLoading(true);
                      setResponseMessage('');
                      try {
                        await requestLoginOtpAndNavigate({
                          type: 'phone',
                          identifier: formData.phone,
                          navigate,
                          sendFn: () => resendOtpByPhone({ phone: formData.phone, purpose: 'login' }),
                        });
                      } catch (err) {
                        const msg = err.response?.data?.msg || err.response?.data?.message;
                        setResponseMessage(msg || 'Could not send OTP. Please try again.');
                      } finally {
                        setLoading(false);
                      }
                    }}
                  >
                    <i className="bi bi-phone"></i>
                    Phone OTP
                  </button>
                  {/* WhatsApp OTP is paused. Login currently uses phone SMS and email only.
                  <span className="login-otp-options__divider">|</span>
                  <button
                    type="button"
                    className={`login-otp-options__btn ${formData.phone.length === 10 ? '' : 'login-otp-options__btn--disabled'}`}
                    disabled={formData.phone.length !== 10 || loading}
                    onClick={async () => {
                      if (formData.phone.length !== 10 || loading) return;
                      setLoading(true);
                      setResponseMessage('');
                      try {
                        await requestLoginOtpAndNavigate({
                          type: 'whatsapp',
                          identifier: formData.phone,
                          navigate,
                          sendFn: async () => {
                            const data = await resendOtpByWhatsApp({ phone: formData.phone, purpose: 'login' });
                            setPreferredNotifyChannel('whatsapp');
                            return data;
                          },
                        });
                      } catch (err) {
                        const msg = err.response?.data?.msg || err.response?.data?.message;
                        setResponseMessage(msg || 'Could not send WhatsApp OTP. Join the Twilio sandbox first, then try again.');
                      } finally {
                        setLoading(false);
                      }
                    }}
                  >
                    <i className="bi bi-whatsapp"></i>
                    WhatsApp OTP
                  </button>
                  */}
                  <span className="login-otp-options__divider">|</span>
                  <button
                    type="button"
                    className={`login-otp-options__btn ${hasValidEmail ? '' : 'login-otp-options__btn--disabled'}`}
                    disabled={!hasValidEmail || loading}
                    onClick={async () => {
                      if (!hasValidEmail || loading) return;
                      setLoading(true);
                      setResponseMessage('');
                      try {
                        await requestLoginOtpAndNavigate({
                          type: 'email',
                          identifier: formData.email.trim(),
                          navigate,
                          sendFn: () => resendOtpByEmail({ email: formData.email.trim(), purpose: 'login' }),
                        });
                      } catch (err) {
                        const msg = err.response?.data?.msg || err.response?.data?.message;
                        setResponseMessage(msg || 'Could not send code. Please try again.');
                      } finally {
                        setLoading(false);
                      }
                    }}
                  >
                    <i className="bi bi-envelope"></i>
                    Email code
                  </button>
                </div>
              </div>
              )}

              {responseMessage && (
                <div className={`responseMessage ${responseIsSuccess ? 'responseMessage--success' : ''}`}>
                  <i className={`bi ${responseIsSuccess ? 'bi-check-circle-fill' : 'bi-exclamation-circle-fill'}`}></i>
                  <span>{responseMessage}</span>
                </div>
              )}

              <button type="submit" className="submitBottomOption" disabled={loading || isPasswordCheckPending}>
                {getSubmitButtonLabel()}
              </button>

              <div className="auth-divider">
                <span className="auth-divider__text">OR</span>
              </div>

              <div className="auth-social-buttons">
                <GoogleSignInButton
                  variant="web"
                  disabled={loading}
                  onError={(msg) => setResponseMessage(msg)}
                />
                <button type="button" className="auth-social-btn auth-social-btn-facebook" onClick={() => showToast({ message: 'Facebook sign-in coming soon' })}>
                  <svg className="auth-social-icon" viewBox="0 0 24 24" width="20" height="20"><path fill="#1877F2" d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                  Continue with Facebook
                </button>
              </div>

              <div className="auth-divider">
                <span className="auth-divider__text">OR</span>
              </div>

              <button type="button" className="guest-btn" onClick={() => setShowGuestForm(true)}>
                LOGIN AS GUEST
              </button>
            </form>

            {showForgotPassword && (
              <ForgotPasswordModal
                channel={usesPhoneIdentifier ? 'phone' : 'email'}
                identifier={usesPhoneIdentifier ? formData.phone : formData.email.trim()}
                onClose={() => setShowForgotPassword(false)}
                onSuccess={(message) => {
                  setShowForgotPassword(false);
                  setFormData((prev) => ({ ...prev, password: '' }));
                  setResponseIsSuccess(true);
                  setResponseMessage(message);
                }}
              />
            )}
            {showGuestForm && (
              <GuestForm onClose={() => setShowGuestForm(false)} onSuccess={() => setShowGuestForm(false)} />
            )}
          </div>

          <div className="get-help">
            Don&apos;t have an account? <Link to="/auth/register">Register</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
